import React, { useEffect, useState, useCallback } from 'react';
import { Power, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Global AI Kill Switch — pulses red when paused, soft green when active.
 * Overrides every WhatsApp routing mode (OFF / WHITELIST / BROADCAST_ONLY /
 * TAGGED_ONLY / ALL) and the in-dashboard AI simulator.
 */
export default function KillSwitchToggle() {
  const [paused, setPaused] = useState(false);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const refresh = useCallback(() => {
    api.get('/ai/kill-switch')
      .then(({ data }) => {
        setPaused(!!data.paused);
        setMeta(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const toggle = async () => {
    setLoading(true);
    try {
      const next = !paused;
      const { data } = await api.post('/ai/kill-switch', { paused: next });
      setPaused(data.paused);
      toast.success(
        data.paused
          ? 'AI globally paused. Every routing mode is now overridden.'
          : 'AI resumed. Routing modes are back in effect.'
      );
      refresh();
    } catch (e) {
      toast.error('Could not toggle AI kill switch.');
    } finally {
      setLoading(false);
      setConfirm(false);
    }
  };

  const onClick = () => {
    if (paused) return toggle();
    setConfirm(true);
  };

  return (
    <>
      <button
        onClick={onClick}
        disabled={loading}
        data-testid="kill-switch-toggle"
        title={
          paused
            ? `Paused by ${meta.paused_by || '—'} at ${meta.paused_at || ''}`
            : 'Click to globally pause AI replies across all routing modes'
        }
        className={[
          'inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12px] font-semibold transition-all',
          'border focus:outline-none focus:ring-2 focus:ring-offset-1',
          paused
            ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 focus:ring-red-300 animate-pulse'
            : 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 focus:ring-emerald-300',
        ].join(' ')}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <span
            className={[
              'w-2 h-2 rounded-full',
              paused ? 'bg-red-500' : 'bg-emerald-500',
            ].join(' ')}
            data-testid="kill-switch-dot"
          />
        )}
        <Power className="w-3.5 h-3.5" />
        <span data-testid="kill-switch-label">
          {paused ? 'AI PAUSED' : 'AI ACTIVE'}
        </span>
      </button>

      {confirm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="kill-switch-confirm-modal"
          onClick={() => !loading && setConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-fab-primary-lighter"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Power className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-[17px] text-fab-text">
                  Globally pause AI?
                </h3>
                <p className="text-[13px] text-fab-text-secondary mt-1.5 leading-relaxed">
                  This overrides <strong>every</strong> WhatsApp routing mode
                  (OFF, WHITELIST, BROADCAST_ONLY, TAGGED_ONLY, ALL). Inbound
                  messages will still be received and routed to your Inbox for
                  manual handling. AI will not auto-reply until you resume.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirm(false)}
                disabled={loading}
                data-testid="kill-switch-cancel"
                className="h-9 px-4 rounded-lg text-[13px] font-semibold text-fab-text-secondary hover:bg-fab-elevated"
              >
                Cancel
              </button>
              <button
                onClick={toggle}
                disabled={loading}
                data-testid="kill-switch-confirm"
                className="h-9 px-4 rounded-lg text-[13px] font-semibold bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2"
              >
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Pause AI globally
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
